import { getAuth } from "firebase/auth";
import {
  collection,
  collectionGroup,
  doc,
  documentId,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import { getToken } from "firebase/app-check";

import store from "../../redux/store";
import { setUserToken } from "../../redux/slices/userSlice";
import { firestore, getAppCheckInstance, server } from "../../config/ConfigFirebase";
import { debugLog } from "../../functions";

// Applicants ("banco de postulantes") live in their own top-level collection,
// keyed by cédula (nuip) for natural idempotency — the same key ContractForm
// uses to resolve a contractor. Each applicant has a `applications/{callId}`
// subcollection (one per call; "general" is the permanent registry).
// Phase 1 (public registration) writes through the intakeApplicant Cloud
// Function; Phase 2 (gestor management) reads directly from Firestore and
// mutates through handleApplicant.
const COLLECTION = "applicants";

// Cloud Function URL for the current environment. `server` is
// `{region}-{projectId}` so each environment resolves to its OWN project's
// functions without hardcoding per-env URLs. Mirrors pqrsdFunctionUrl.
export const applicantFunctionUrl = (fn) =>
  `https://${server}.cloudfunctions.net/${fn}`;

// Firestore Timestamps are non-serializable for Redux; normalize to ISO.
const toIso = (v) => {
  if (!v) return null;
  if (typeof v === "string") return v;
  if (v.toDate) return v.toDate().toISOString();
  if (v instanceof Date) return v.toISOString();
  return null;
};

const normalizeDocs = (documents = {}) =>
  Object.fromEntries(
    Object.entries(documents).map(([type, info]) => [
      type,
      info && typeof info === "object"
        ? {
            ...info,
            uploadedAt: toIso(info.uploadedAt),
            expiryDate: toIso(info.expiryDate),
            validatedAt: toIso(info.validatedAt),
          }
        : info,
    ])
  );

const mapApplicant = (snap) => {
  const data = snap.data();
  return {
    ...data,
    nuip: data.nuip || snap.id,
    createdAt: toIso(data.createdAt),
    updatedAt: toIso(data.updatedAt),
    selectedAt: toIso(data.selectedAt),
    photoUpdatedAt: toIso(data.photoUpdatedAt),
    documents: normalizeDocs(data.documents),
  };
};

const mapApplication = (snap) => {
  const data = snap.data();
  return {
    ...data,
    // Collection-group results arrive with no parent context, so fall back to the
    // owning applicant's doc id (the cédula).
    nuip: data.nuip || snap.ref.parent?.parent?.id || null,
    callId: data.callId || snap.id,
    callName: data.callName || data.callId || snap.id,
    createdAt: toIso(data.createdAt),
    updatedAt: toIso(data.updatedAt),
    selectedAt: toIso(data.selectedAt),
    documents: normalizeDocs(data.documents),
  };
};

// Maps an intake error code to a citizen-facing Spanish message. Returns null
// for unknown codes so the caller can fall back to a generic message.
export const applicantErrorMessage = (code) => ({
  ENCRYPTED_FILE: "Uno de los archivos está protegido o encriptado. Ábrelo en tu navegador (Chrome o Edge), usa Imprimir → «Guardar como PDF» para generar una copia sin cifrado y vuelve a subir ese archivo.",
  FILE_TOO_LARGE: "Uno de los archivos supera el tamaño máximo permitido.",
  TOO_MANY_FILES_FOR_TYPE: "Adjuntaste más archivos de los permitidos para un documento.",
  DOC_AT_MAX_FILES: "Ya alcanzaste el número máximo de archivos de ese documento. Usa «Reemplazar» sobre el archivo que quieras cambiar.",
  UNKNOWN_REPLACE_TARGET: "El archivo que intentas reemplazar ya no existe. Recarga la página y vuelve a intentarlo.",
  DUPLICATE_REPLACE_TARGET: "Adjuntaste dos archivos para reemplazar el mismo documento. Deja solo uno.",
  UNKNOWN_DOC_TYPE: "Uno de los documentos no está habilitado para esta etapa.",
  UNSUPPORTED_MIME: "Uno de los archivos tiene un formato no permitido.",
  TOO_MANY_FILES: "Adjuntaste demasiados archivos de una sola vez. Guarda los que ya tienes y sube el resto en un segundo intento.",
  TOO_MANY_FIELDS: "Adjuntaste demasiados archivos de una sola vez. Guarda los que ya tienes y sube el resto en un segundo intento.",
  UNKNOWN_PROFILE_ENTRY: "Un soporte quedó asociado a un registro que ya no existe. Vuelve a adjuntarlo en la formación o experiencia correspondiente.",
  UNSUPPORTED_PHOTO_MIME: "La foto de perfil debe ser una imagen (JPG o PNG).",
  PHOTO_TOO_LARGE: "La foto de perfil supera el tamaño máximo permitido.",
  EMAIL_NOT_VERIFIED: "Debes verificar tu correo electrónico para continuar.",
  PHONE_NOT_VERIFIED: "Debes verificar tu celular para continuar.",
  EMAIL_IN_USE: "Este correo ya está registrado con otra cédula.",
  PHONE_IN_USE: "Este celular ya está registrado con otra cédula.",
}[code] || null);

// --- Phase 1: public registration -----------------------------------------

// Fresh App Check token (forceRefresh), same pattern PqrsdForm uses.
async function appCheckToken() {
  const instance = getAppCheckInstance();
  if (!instance) throw new Error("App Check no está inicializado");
  const { token } = await getToken(instance, true);
  if (!token) throw new Error("Token de App Check no disponible");
  return token;
}

// Files a public applicant registration (a step of the postulación) via
// intakeApplicant. `data` carries the personal/academic/experience fields;
// `callId` scopes the postulación (e.g. "general"); `stage` is 1|2;
// `step` records progress for resume ("perfil" | "documents" | "aplicar");
// `files` is [{ type, file, scope, entryId }] — `scope: "profile"` files belong
// to the PERSON (identity, and each formación / experiencia certificate, which
// also carries the `entryId` of the entry it certifies) and are shared by every
// convocatoria; anything else is filed under this postulación.
// `photo` is an optional profile picture (a File),
// sent in its own multipart field so it stays out of the per-call documents map;
// `removePhoto` clears the stored one. Returns { ok, status, json }.
export const registerApplicant = async ({
  data,
  callId,
  stage = 1,
  step,
  files = [],
  photo = null,
  removePhoto = false,
  idempotencyKey,
  phoneToken,
} = {}) => {
  try {
    const intakeUrl =
      process.env.REACT_APP_APPLICANT_INTAKE_URL ||
      applicantFunctionUrl("intakeApplicant");

    const token = await appCheckToken();
    const payload = {
      applicant: data,
      callId,
      stage,
      step,
      // One descriptor per file, index-aligned with files[]. It rides in the JSON
      // payload rather than in extra form fields because busboy caps the request
      // at MAX_FIELDS — three parallel arrays would blow that budget.
      fileMeta: files.map(({ type, scope, entryId, replaceFileId }) => ({
        type,
        scope: scope === "profile" ? "profile" : "application",
        ...(entryId ? { entryId } : {}),
        // Names the stored file this upload replaces. Without it the upload adds
        // a new file — which is what made "Reemplazar archivo" leave the rejected
        // one in place and the document stuck at RECHAZADO.
        ...(replaceFileId ? { replaceFileId } : {}),
      })),
      // Travels in the JSON payload rather than its own form field: busboy caps
      // the request at MAX_FIELDS.
      ...(removePhoto ? { removePhoto: true } : {}),
      meta: { source: "submission-form", version: "2.1" },
    };

    const fd = new FormData();
    fd.append("payload", JSON.stringify(payload));
    // files[] preserves order; types[] is kept alongside fileMeta so an older
    // backend still files application-scoped documents correctly.
    files.forEach(({ type, file }) => {
      fd.append("files[]", file);
      fd.append("types[]", type);
    });
    // Its own field name, appended last so it never shifts the files[]/types[]
    // pairing the backend relies on.
    if (photo) fd.append("photo", photo, photo.name || "photo.jpg");

    const headers = { "X-Firebase-AppCheck": token };
    if (idempotencyKey) headers["X-Idempotency-Key"] = idempotencyKey;
    // Firebase Phone Auth ID token proving the celular was verified. Sent in its
    // own header — NOT Authorization, which the backend reads as a staff Bearer
    // token and would use to skip the verification gate.
    if (phoneToken) headers["X-Phone-Token"] = phoneToken;

    const response = await fetch(intakeUrl, { method: "POST", body: fd, headers });
    const json = await response.json().catch(() => ({}));
    return { ok: response.ok, status: response.status, json };
  } catch (error) {
    debugLog(error);
    return { ok: false, status: 0, json: { result: false, error: error.message } };
  }
};

// --- Resume: OTP login (email/phone) ---------------------------------------

// Posts files to a public certificate reader (App Check protected) and returns
// { extraction } or { error: CODE }. Nothing is stored — the files still travel
// with registerApplicant / the profile save.
const readCertificates = async (fn, files) => {
  try {
    const token = await appCheckToken();
    const body = new FormData();
    Object.entries(files).forEach(([field, file]) => {
      if (file) body.append(field, file, file.name || field);
    });
    const response = await fetch(applicantFunctionUrl(fn), {
      method: "POST",
      headers: { "X-Firebase-AppCheck": token },
      body,
    });
    const json = await response.json().catch(() => ({}));
    if (json && json.result && json.extraction) return { extraction: json.extraction };
    return { error: (json && json.error) || `HTTP_${response.status}` };
  } catch (error) {
    debugLog(error);
    return { error: "NETWORK_ERROR" };
  }
};

// Reads a work-experience certificate (PDF / image) with AI:
// { extraction: { company, nit, periods[], confidence } } or { error }.
export const extractExperienceCertificate = (file) =>
  readCertificates("extractExperienceCertificate", { file });

// Reads an acta de grado and/or a diploma (PDF / image, same formación) with AI
// in one request: { extraction: { level, degree, institution, institutionRead,
// institutionMatched, graduationDate, inProgress, foreignDegree, convalidated,
// confidence } } or { error }.
export const extractEducationCertificate = ({ acta, diploma } = {}) =>
  readCertificates("extractEducationCertificate", { acta, diploma });

// Requests an OTP for the given recipient. `channel` is 'EMAIL' | 'SMS'
// (SMS deferred behind a provider). Protected by App Check. Returns
// { ok, json } where json = { result, expiresInMinutes } on success.
export const sendApplicantOtp = async ({ recipient, channel = "EMAIL" } = {}) => {
  try {
    const token = await appCheckToken();
    const response = await fetch(applicantFunctionUrl("applicantOtp"), {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Firebase-AppCheck": token },
      body: JSON.stringify({ action: "SEND", recipient, channel }),
    });
    const json = await response.json().catch(() => ({}));
    return { ok: response.ok, json };
  } catch (error) {
    debugLog(error);
    return { ok: false, json: { result: false, error: error.message } };
  }
};

// Verifies an OTP code. On success returns { ok, json } where json carries a
// short-lived resume `sessionToken` and the resolved `nuip`.
export const verifyApplicantOtp = async ({ recipient, code } = {}) => {
  try {
    const token = await appCheckToken();
    const response = await fetch(applicantFunctionUrl("applicantOtp"), {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Firebase-AppCheck": token },
      body: JSON.stringify({ action: "VERIFY", recipient, code }),
    });
    const json = await response.json().catch(() => ({}));
    return { ok: response.ok, json };
  } catch (error) {
    debugLog(error);
    return { ok: false, json: { result: false, error: error.message } };
  }
};

// Resolves an applicant from a Firebase Phone Auth ID token (the phone
// continuation path). The client verifies the phone via Firebase Phone Auth,
// then passes the resulting ID token here. On success returns { ok, json } where
// json carries the resolved `nuip` and a short-lived resume `sessionToken`.
export const resolvePhoneApplicant = async ({ phoneToken } = {}) => {
  try {
    const token = await appCheckToken();
    const response = await fetch(applicantFunctionUrl("applicantOtp"), {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Firebase-AppCheck": token },
      body: JSON.stringify({ action: "RESOLVE_PHONE", phoneToken }),
    });
    const json = await response.json().catch(() => ({}));
    return { ok: response.ok, json };
  } catch (error) {
    debugLog(error);
    return { ok: false, json: { result: false, error: error.message } };
  }
};

// Contact-availability pre-check (registration). Given a cédula and an email
// and/or phone (E.164), reports whether either already belongs to a DIFFERENT
// applicant — so the form can fail fast before spending an OTP/SMS. Returns
// { ok, json } where json = { result, emailInUse, phoneInUse }.
export const checkApplicantContact = async ({ nuip, email, phone } = {}) => {
  try {
    const token = await appCheckToken();
    const response = await fetch(applicantFunctionUrl("applicantOtp"), {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Firebase-AppCheck": token },
      body: JSON.stringify({ action: "CHECK_CONTACT", nuip, email, phone }),
    });
    const json = await response.json().catch(() => ({}));
    return { ok: response.ok, json };
  } catch (error) {
    debugLog(error);
    return { ok: false, json: { result: false, error: error.message } };
  }
};

// Public URL for an applicant document. Applicant files are uploaded public-read
// (intakeApplicant: predefinedAcl "publicRead"), so no signed URL / backend call
// is needed — mirror the URL the gestor GET_DOC_URL builds. `bucketName` comes
// from getApplicantConfig().storageBucket; `path` is the file's GCS object
// path from getApplication(). Encodes per path segment so slashes stay separators.
export const applicantDocPublicUrl = (bucketName, filePath) => {
  if (!bucketName || !filePath) return null;
  const encoded = String(filePath).split("/").map(encodeURIComponent).join("/");
  return `https://storage.googleapis.com/${bucketName}/${encoded}`;
};

// --- Phase 2: authenticated gestor management ------------------------------

// Bearer-token call to handleApplicant. Mirrors ContractService.handleContract:
// on 401 refresh the cached Redux token.
const handleApplicant = async (token, body) => {
  const response = await fetch(applicantFunctionUrl("handleApplicant"), {
    method: "post",
    body: JSON.stringify(body),
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
  });

  if (response.status === 401) {
    const auth = getAuth();
    const refreshed = await auth.currentUser.getIdToken();
    store.dispatch(setUserToken(refreshed));
  }

  return response;
};

// Cursor-paginated fetch for the gestor list (mirror fetchPqrsdPage).
export const fetchApplicantPage = async ({
  documentationStatus,
  dateFrom,
  dateTo,
  cursor,
  pageSize = 25,
} = {}) => {
  try {
    const constraints = [];
    if (documentationStatus) {
      constraints.push(where("documentationStatus", "==", documentationStatus));
    }
    if (dateFrom) constraints.push(where("createdAt", ">=", dateFrom));
    if (dateTo) constraints.push(where("createdAt", "<=", dateTo));
    constraints.push(orderBy("createdAt", "desc"));
    if (cursor) constraints.push(startAfter(cursor));
    constraints.push(limit(pageSize + 1)); // one extra to detect hasMore
    const snap = await getDocs(query(collection(firestore, COLLECTION), ...constraints));
    const pageDocs = snap.docs.slice(0, pageSize);
    return {
      items: pageDocs.map(mapApplicant),
      lastDoc: pageDocs[pageDocs.length - 1] || null,
      hasMore: snap.docs.length > pageSize,
    };
  } catch (e) {
    console.error("fetchApplicantPage error:", e);
    // `error` viaja al listado para que un fallo (índice construyéndose, permisos)
    // no se muestre como "sin resultados".
    return { items: [], lastDoc: null, hasMore: false, error: e?.code || "unknown" };
  }
};

// Applicant profiles for a set of cédulas, as a Map keyed by nuip. Firestore's
// `in` operator caps at 30 values, so ids are read in chunks — one query per
// chunk instead of one getDoc per applicant.
const fetchApplicantsByIds = async (ids) => {
  const unique = [...new Set(ids.filter(Boolean).map(String))];
  const byNuip = new Map();
  for (let i = 0; i < unique.length; i += 30) {
    const chunk = unique.slice(i, i + 30);
    const snap = await getDocs(
      query(collection(firestore, COLLECTION), where(documentId(), "in", chunk))
    );
    snap.docs.forEach((d) => byNuip.set(d.id, mapApplicant(d)));
  }
  return byNuip;
};

// Cursor-paginated fetch of ONE convocatoria's postulaciones, hydrated with the
// applicant's identity fields. Mirrors fetchApplicantPage's signature and return
// shape so the list can swap fetchers without touching its pagination logic.
// Rows carry the CALL's own progress (completion, documentación, fecha de
// postulación) rather than the applicant-level rollup.
export const fetchApplicationPage = async ({
  callId,
  documentationStatus,
  dateFrom,
  dateTo,
  cursor,
  pageSize = 25,
} = {}) => {
  if (!callId) return { items: [], lastDoc: null, hasMore: false };
  try {
    const constraints = [where("callId", "==", String(callId))];
    if (documentationStatus) {
      constraints.push(where("documentationStatus", "==", documentationStatus));
    }
    if (dateFrom) constraints.push(where("createdAt", ">=", dateFrom));
    if (dateTo) constraints.push(where("createdAt", "<=", dateTo));
    constraints.push(orderBy("createdAt", "desc"));
    if (cursor) constraints.push(startAfter(cursor));
    constraints.push(limit(pageSize + 1)); // one extra to detect hasMore
    const snap = await getDocs(query(collectionGroup(firestore, "applications"), ...constraints));
    const pageDocs = snap.docs.slice(0, pageSize);
    const applications = pageDocs.map(mapApplication);
    const profiles = await fetchApplicantsByIds(applications.map((p) => p.nuip));
    const items = applications
      .map((p) => {
        const profile = profiles.get(String(p.nuip));
        if (!profile) return null; // applicant doc gone — nothing to show
        // La completitud que importa es la de la hoja de vida (documentos de
        // perfil): la de la postulación sólo mide los documentos propios de la
        // convocatoria y da 100% cuando no pide ninguno. Su documentationStatus
        // sí se muestra, pero sólo cuando la convocatoria pide documentos.
        const callHasDocs = Object.keys(p.documents || {}).length > 0;
        return {
          ...profile,
          completionPercentage: profile.completionPercentage,
          documentationStatus: callHasDocs
            ? (p.documentationStatus || profile.documentationStatus)
            : profile.documentationStatus,
          createdAt: p.createdAt, // fecha de postulación a esta convocatoria
          // "Seleccionado" acotado a ESTA convocatoria, no al rollup del aspirante.
          selected: p.selected === true,
          selectedAt: p.selectedAt || null,
          callId: p.callId,
          callName: p.callName,
          applicationStatus: p.status || null,
        };
      })
      .filter(Boolean);
    return {
      items,
      lastDoc: pageDocs[pageDocs.length - 1] || null,
      hasMore: snap.docs.length > pageSize,
    };
  } catch (e) {
    console.error("fetchApplicationPage error:", e);
    return { items: [], lastDoc: null, hasMore: false, error: e?.code || "unknown" };
  }
};

export const subscribeApplicantList = ({ documentationStatus } = {}, cb) => {
  const constraints = [];
  if (documentationStatus) {
    constraints.push(where("documentationStatus", "==", documentationStatus));
  }
  constraints.push(orderBy("createdAt", "desc"), limit(200));
  const q = query(collection(firestore, COLLECTION), ...constraints);
  return onSnapshot(q, (snap) => cb(snap.docs.map(mapApplicant)));
};

export const getApplicant = async (nuip) => {
  try {
    const snap = await getDoc(doc(firestore, COLLECTION, String(nuip)));
    if (!snap.exists()) return null;
    return mapApplicant(snap);
  } catch (e) {
    console.error("getApplicant error:", e);
    return null;
  }
};

export const getApplication = async (nuip, callId) => {
  try {
    const snap = await getDoc(
      doc(firestore, COLLECTION, String(nuip), "applications", String(callId))
    );
    if (!snap.exists()) return null;
    return mapApplication(snap);
  } catch (e) {
    console.error("getApplication error:", e);
    return null;
  }
};

export const getApplications = async (nuip) => {
  try {
    const snap = await getDocs(
      collection(firestore, COLLECTION, String(nuip), "applications")
    );
    return snap.docs.map(mapApplication);
  } catch (e) {
    console.error("getApplications error:", e);
    return [];
  }
};

// Validate (approve/reject) a document. `status` is 'VALIDATED' | 'REJECTED';
// rejection requires `comments`. `callId` selects the call the
// document belongs to. `fileId` (optional) targets one file of a multi-file
// document; omitted = applies to every file of the type. `scope` is "profile"
// for person-level documents (identity / entry certificates), which ignore
// callId. Audited server-side.
export const validateDocument = async (nuip, type, status, user, comments = null, callId, fileId = null, scope = "application") => {
  try {
    const body = { action: "VALIDATE_DOCUMENT", nuip: String(nuip), type, status, comments, callId, fileId, scope };
    let response = await handleApplicant(user.token, body);
    response = await response.json();
    return response.result ? response.body : null;
  } catch (error) {
    debugLog(error);
    return null;
  }
};

// Bitácora del aspirante (colección applicantAudit): registro público de intake
// y acciones del gestor, más reciente primero. Devuelve [] ante cualquier fallo
// para que el drawer muestre "sin eventos" en vez de romperse.
export const getApplicantLogs = async (nuip, user) => {
  try {
    const body = { action: "GET_APPLICANT_LOGS", nuip: String(nuip) };
    let response = await handleApplicant(user.token, body);
    response = await response.json();
    return response.result ? (response.body || []) : [];
  } catch (error) {
    debugLog(error);
    return [];
  }
};

// Promote an applicant to contractor: sets the postulación to SELECTED and triggers
// the stage-2 prompt email asking for the remaining documents. Returns a
// contractor-shaped payload (truthy = success); nothing consumes it yet — it's the
// hook for the not-yet-implemented prefilled-contract flow.
export const selectContractor = async (nuip, user, { callId, contractCode = null } = {}) => {
  try {
    const body = { action: "SELECT_CONTRACTOR", nuip: String(nuip), callId, contractCode };
    let response = await handleApplicant(user.token, body);
    response = await response.json();
    return response.result ? response.body : null;
  } catch (error) {
    debugLog(error);
    return null;
  }
};

// Public URL for one document, resolved server-side. Docs are stored public-read,
// so this returns a plain storage.googleapis.com URL (no signing/expiry). The name
// is kept for compatibility; it's now a slight misnomer (no longer a signed URL).
// `fileId` (optional) selects one file of a multi-file document.
export const getDocumentSignedUrl = async (nuip, type, user, callId, fileId = null, scope = "application") => {
  try {
    const body = { action: "GET_DOC_URL", nuip: String(nuip), type, callId, fileId, scope };
    let response = await handleApplicant(user.token, body);
    response = await response.json();
    return response.result ? response.body?.url : null;
  } catch (error) {
    debugLog(error);
    return null;
  }
};
